import styled from '@emotion/styled'

import { DynamicShiftStatus, ShiftActivity, UnitRelation } from '@bemlo/enums'
import { OnCallTime, shiftWorkDuration } from '@bemlo/schedule'

import { ShiftCardColor, useShiftCardColors } from './useShiftCardColors'
import { useShiftStatusIconName } from './useShiftStatusIconName'
import { useUnitRelationBadge } from './useUnitRelationBadge'
import { Color, theme } from '../../constants'
import { useTranslations } from '../../translate'
import { Avatar } from '../Avatar'
import { Badge } from '../Badge'
import { Clickable } from '../Clickable'
import { Div } from '../Div'
import { Icon } from '../Icon'
import { SHIFT_ACTIVITY_COLORS } from '../Shift/activity'
import { ShiftStatusBadge } from '../ShiftStatusBadge'
import { Text } from '../Text'

const StyledClickable = styled(Clickable)({
  display: 'flex',
  minHeight: 130,
  boxShadow: theme.boxShadow.normal,
  borderRadius: theme.border.radius.normal,
  backgroundColor: Color.WHITE,

  '&:hover': {
    filter: 'brightness(98%)',
  },

  '&:active': {
    filter: 'brightness(95%)',
  },
})

const LEFT_BORDER_WIDTH = theme.border.radius.normal

const StatusBorder = styled(Div)<{ colors: ShiftCardColor }>(({ colors }) => ({
  borderBottomLeftRadius: theme.border.radius.normal,
  borderTopLeftRadius: theme.border.radius.normal,
  backgroundColor: colors.statusBorder,
  width: LEFT_BORDER_WIDTH,
}))

const Left = styled(Div)<{ colors: ShiftCardColor; isUrgent: boolean }>(
  ({ colors, isUrgent }) => ({
    display: 'flex',
    flexDirection: 'column',
    borderTop: isUrgent ? 'none' : `1px solid ${colors.borderOuter}`,
    borderBottom: `1px solid ${colors.borderOuter}`,
    position: 'relative',
    width: 50,
  }),
)

const Urgent = styled(Div)({
  backgroundColor: Color.RED,
  color: Color.WHITE,
  fontSize: theme.font.size['6.5'],
  textAlign: 'center',
  fontWeight: theme.font.weight.bold,
  paddingTop: 1,
  paddingBottom: 1,
  paddingRight: LEFT_BORDER_WIDTH,
})

const LeftContent = styled(Div)<{ colors: ShiftCardColor }>(({ colors }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'end',
  padding: theme.spacing[3],
  borderRight: `1px solid ${colors.borderInner}`,
  backgroundColor: colors.background,
}))

const Date = styled(Div)<{ colors: ShiftCardColor }>(({ colors }) => ({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  color: colors.text,
}))

const Right = styled(Div)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[1],
  borderTop: theme.border.style.thin,
  borderRight: theme.border.style.thin,
  borderBottom: theme.border.style.thin,
  borderTopRightRadius: theme.border.radius.normal,
  borderBottomRightRadius: theme.border.radius.normal,
  padding: theme.spacing[3],
  overflow: 'hidden',
})

const TimeRow = styled(Div)({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing[3],
  containerType: 'inline-size',
})

const TimeText = styled(Text)`
  @container (max-width: 300px) {
    font-size: ${theme.font.size[5]}px;
  }
`

const LastRow = styled(Div)({
  display: 'flex',
  alignItems: 'center',
  marginTop: 'auto',
  gap: theme.spacing[2],
  paddingTop: theme.spacing[2],
})

type ShiftCardProps = {
  className?: string
  shift: Shift
  pricing?: string
  user: {
    name: string
    image?: string
  }
  onClick: () => void
}

type Unit = {
  address: string
  name: string
  organizationName: string
  relation?: UnitRelation
}

type ScheduleResource = {
  name: string
}

type Shift = {
  date: string
  activity: ShiftActivity
  startTime: string
  endTime: string
  breakTime: number
  onCallTime: OnCallTime | null | undefined
  unit: Unit
  scheduleResource: ScheduleResource
  status: DynamicShiftStatus
  isUrgent: boolean
}

export const ShiftCard = ({
  className,
  shift,
  pricing,
  user,
  onClick,
}: ShiftCardProps) => {
  const t = useTranslations()

  const colors = useShiftCardColors(shift.status, shift.isUrgent)
  const shiftStatusIconName = useShiftStatusIconName(
    shift.status,
    shift.isUrgent,
  )
  const unitRelationBadge = useUnitRelationBadge(shift.unit.relation)

  const duration = shiftWorkDuration(shift).workTime
  const durationString = t.$date.formatDuration({ hours: duration.asHours() })
  const date = t.$date(shift.date)

  return (
    <StyledClickable className={className} onClick={onClick}>
      <StatusBorder colors={colors} />

      <Left colors={colors} isUrgent={shift.isUrgent}>
        {shift.isUrgent && <Urgent>{t.shifts.urgent()}</Urgent>}

        <LeftContent colors={colors}>
          {shiftStatusIconName && (
            <Icon name={shiftStatusIconName} color={colors.text} />
          )}
        </LeftContent>

        <Date colors={colors}>
          <Text size={5}>{date.format('MMM').toUpperCase()}</Text>
          <Text size={2} fontWeight={theme.font.weight.bold}>
            {date.date()}
          </Text>
        </Date>
      </Left>

      <Right>
        <TimeRow>
          <TimeText size={3} fontWeight={theme.font.weight.bold}>
            {date.format('ddd')},{' '}
            {t.$date.time(shift.startTime).formatTimeRange(shift.endTime)}
          </TimeText>
          <Text color={Color.MEDIUM_BLUE} size={5}>
            {durationString}
          </Text>
        </TimeRow>

        <Text
          size={6.5}
          fontWeight={theme.font.weight.bold}
          color={Color.DARKER_GREY}
          noWrap
        >
          {shift.scheduleResource.name}
          &nbsp;&nbsp;&bull;&nbsp;&nbsp;
          {shift.unit.name}
          &nbsp;&nbsp;/&nbsp;&nbsp;
          {shift.unit.organizationName}
        </Text>

        <Text size={6.5} color={Color.DARK_GREY} noWrap>
          {shift.unit.address}
        </Text>

        <LastRow>
          <ShiftStatusBadge status={shift.status} />
          {shift.activity !== ShiftActivity.WORK && (
            <Badge
              size="sm"
              color={SHIFT_ACTIVITY_COLORS[shift.activity].badge}
              icon={SHIFT_ACTIVITY_COLORS[shift.activity].icon}
            >
              {t.$shiftActivity(shift.activity)}
            </Badge>
          )}
          {shift.status === DynamicShiftStatus.BOOKED && (
            <Avatar size="sm" image={user.image} name={user.name} />
          )}
          {pricing && <Badge size="sm">{pricing}</Badge>}
          {unitRelationBadge && (
            <Badge
              color={unitRelationBadge.badgeColor}
              styles={{ ml: 'auto' }}
              size="sm"
            >
              <Icon
                size="sm"
                design="solid"
                name={unitRelationBadge.iconName}
              />
              {unitRelationBadge.text}
            </Badge>
          )}
        </LastRow>
      </Right>
    </StyledClickable>
  )
}
